import { Link } from "react-router-dom";
import { ArrowRight, Feather, LineChart, Sparkles, BookOpen } from "lucide-react";

const pillars = [
  {
    icon: Feather,
    title: "A quiet daily log",
    body: "Two minutes to notice the night, the fog, the fire. No pink. No forms. Just your own voice on the page.",
  },
  {
    icon: LineChart,
    title: "Patterns, gently surfaced",
    body: "After a week of check-ins, Meridia begins to notice what you cannot — how your sleep, mood, and body speak to each other.",
  },
  {
    icon: Sparkles,
    title: "A companion that listens",
    body: "Ask the 3 a.m. questions. Meridia answers with the warmth of someone who has read every page of the evidence.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="leaf-mark" />
          <span className="font-serif-display text-2xl">Meridia</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/signin" className="text-sm text-[#52443E] hover:text-[#2C221E] px-4 py-2" data-testid="landing-signin-link">
            Sign in
          </Link>
          <Link to="/signup" className="btn-primary text-sm" data-testid="landing-signup-link">
            Begin
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-20 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 rise-stagger">
            <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold" data-testid="hero-eyebrow">
              A companion for perimenopause & menopause
            </p>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#2C221E]" data-testid="hero-headline">
              Lying awake at 3 a.m. again. <br className="hidden sm:block" />
              <span className="italic text-[#C26D53]">You are not losing your mind.</span>
            </h1>
            <p className="mt-7 text-lg text-[#52443E] leading-relaxed max-w-xl" data-testid="hero-sub">
              Your hormones are changing — and no one told you what that would feel like. Meridia is a private place to track the shifts, see the patterns, and ask the questions you did not know you could ask.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Begin your journal <ArrowRight size={16} />
              </Link>
              <Link to="/signin" className="btn-ghost" data-testid="hero-cta-secondary">
                I already have an account
              </Link>
            </div>
            <p className="mt-6 text-sm text-[#8C7A70]">
              Private by default. Your words stay with you.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative card-paper paper-grain overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1607557997149-96bf2b30c3c3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                alt="Morning light on a notebook"
                className="w-full h-[480px] object-cover rounded-3xl"
              />
              <div className="absolute left-6 right-6 bottom-6 bg-[#FFFDF9]/95 backdrop-blur-md border border-[#E5D9C8] rounded-2xl p-5">
                <p className="font-serif-display text-lg leading-snug text-[#2C221E] italic">
                  "The nights, the fog, the fire — met with the knowing you deserve."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F4EDE2]/60 border-y border-[#EAE0D2]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-2xl">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2C221E] leading-tight">
              A grown-up space for a stage of life that deserves one.
            </h2>
            <p className="mt-5 text-[#52443E] text-lg">
              Not another cycle app. Not another wellness feed. Something quieter — closer to a well-made notebook than a dashboard.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {pillars.map((p) => (
              <div key={p.title} className="card-paper p-7 paper-grain" data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-11 h-11 rounded-full bg-[#F9ECE8] flex items-center justify-center text-[#C26D53]">
                  <p.icon size={20} />
                </div>
                <h3 className="mt-5 font-serif-display text-xl text-[#2C221E]">{p.title}</h3>
                <p className="mt-3 text-[#52443E] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img
              src="https://images.unsplash.com/photo-1578583316094-043603e4386c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
              alt="Soft leaf shadows on a warm wall"
              className="rounded-3xl w-full h-[420px] object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#2C221E]">
              Millions of women feel this way. <br />
              Almost none were told what helps.
            </h2>
            <p className="mt-6 text-lg text-[#52443E] leading-relaxed">
              Meridia pairs a private symptom journal with a companion grounded in current menopause science — the kind of guidance you would get from a thoughtful friend who happens to have read everything.
            </p>
            <div className="mt-8">
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="section-cta-signup">
                Start your first entry <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#EAE0D2]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="leaf-mark" />
            <span className="font-serif-display text-lg">Meridia</span>
          </div>
          <p className="text-sm text-[#8C7A70] max-w-md">
            Meridia offers companionship and education — not medical advice. Please speak with a menopause-informed clinician for care decisions.
          </p>
        </div>
      </footer>
    </div>
  );
}
